import { NavLink } from 'react-router-dom'
import { useAuthStore } from '@/store/authStore'
import {
  IconOverview,
  IconActivity,
  IconSecurity,
  IconOrganizations,
  IconUsers,
  IconDocuments,
  IconSignatures,
  IconAnalytics,
} from './Icons'

const monitorLinks = [
  { to: '/dashboard', label: 'Overview', icon: IconOverview },
  { to: '/activity', label: 'Activity Feed', icon: IconActivity, badge: 'Live' },
  { to: '/security', label: 'Security Events', icon: IconSecurity },
]

const dataLinks = [
  { to: '/organizations', label: 'Organizations', icon: IconOrganizations },
  { to: '/users', label: 'Platform Users', icon: IconUsers },
  { to: '/documents', label: 'Documents', icon: IconDocuments },
  { to: '/signatures', label: 'Signatures', icon: IconSignatures },
]

const systemLinks = [
  { to: '/analytics', label: 'Analytics & Insights', icon: IconAnalytics },
]

function NavGroup({ label, links }: { label: string; links: typeof monitorLinks }) {
  return (
    <div className="mb-5">
      <div className="mb-2 px-3 font-mono text-[10px] font-bold uppercase tracking-widest text-slate-400">
        {label}
      </div>
      <div className="space-y-1">
        {links.map((link) => {
          const Icon = link.icon
          return (
            <NavLink
              key={link.to}
              to={link.to}
              className={({ isActive }) =>
                `group relative flex items-center justify-between rounded-xl px-3 py-2.5 text-xs transition-all duration-150 ${
                  isActive
                    ? 'bg-sky-50 text-sky-800 font-bold border border-sky-200/80 shadow-xs'
                    : 'text-slate-600 hover:bg-slate-50/90 hover:text-slate-900 border border-transparent font-medium'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <div className="flex items-center gap-3">
                    {isActive && (
                      <span className="absolute -left-1 top-1/2 h-5 w-1 -translate-y-1/2 rounded-r-full bg-sky-600 shadow-sm" />
                    )}
                    <Icon className={`h-4 w-4 transition-transform group-hover:scale-110 ${isActive ? 'text-sky-700' : 'text-slate-400 group-hover:text-slate-700'}`} />
                    <span className="tracking-tight">{link.label}</span>
                  </div>
                  {link.badge && (
                    <span
                      className={`rounded px-1.5 py-0.5 font-mono text-[9px] font-bold ${
                        isActive
                          ? 'bg-sky-200/70 text-sky-800'
                          : 'bg-slate-100 text-slate-500 group-hover:bg-slate-200/70 group-hover:text-slate-700'
                      }`}
                    >
                      {link.badge}
                    </span>
                  )}
                </>
              )}
            </NavLink>
          )
        })}
      </div>
    </div>
  )
}

export function Sidebar({ open, onClose }: { open: boolean; onClose: () => void }) {
  const admin = useAuthStore((s) => s.admin)

  return (
    <>
      {/* Mobile/tablet backdrop */}
      {open && (
        <div className="fixed inset-0 z-30 bg-slate-900/40 backdrop-blur-sm lg:hidden" onClick={onClose} aria-hidden="true" />
      )}
      <aside
        className={`fixed inset-y-0 left-0 z-40 flex h-screen w-64 shrink-0 flex-col border-r border-slate-200 bg-white p-4 transition-transform duration-200 lg:static lg:h-screen lg:translate-x-0 shadow-xs ${
          open ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Brand Header */}
        <div className="mb-6 px-2">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-sky-600 to-blue-700 shadow-md text-white font-display text-base font-extrabold">
              V
            </div>
            <div>
              <div className="font-display text-sm font-bold tracking-tight text-slate-900">
                VELOSCALAR <span className="text-sky-600 font-extrabold">SIGN</span>
              </div>
              <div className="font-mono text-[10px] font-semibold tracking-wider text-slate-400">ADMIN PORTAL</div>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto pr-1">
          <NavGroup label="Monitoring" links={monitorLinks} />
          <NavGroup label="Data Core" links={dataLinks} />
          <NavGroup label="Intelligence" links={systemLinks} />
        </nav>

        {/* User Footer */}
        <div className="mt-auto border-t border-slate-200 pt-3.5">
          <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50/80 p-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-sky-100 to-blue-100 font-mono text-xs font-bold text-sky-700 border border-sky-200 shadow-2xs">
              {admin?.name?.slice(0, 2).toUpperCase() ?? 'AD'}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-xs font-bold text-slate-900">{admin?.name ?? 'Administrator'}</div>
              <div className="truncate font-mono text-[10px] text-slate-500">{admin?.email ?? 'admin@veloscalar.com'}</div>
            </div>
          </div>
        </div>
      </aside>
    </>
  )
}
